import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { StatsigProvider } from '@statsig/react-bindings';
import { switchToBaseUser, switchUserBroadcastSucceeded, userFetch } from '@redux/slices/user';
import { CHANNELS } from '@constants';

import { Announcement, LoaderPage, Notification } from '@utilities';

import Footer from '../../components/Footer';
import Header from './components/Header';
import Maintenance from './components/Maintenance';
import ModalOnboarding from './components/ModalOnboarding';
import Router from './components/Router';

import styles from './_index.module.scss';

export const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const baseProxySwitchChannel = useRef(null);

  const user = useSelector((state) => state?.user);
  const { baseAccountUser, clientInfo, email, firstName, id, lastName, permissions, routes } =
    user?.data;
  const isBaseProxySwitched = user?.isBaseProxySwitched;
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';
  const baseAccountId = baseAccountUser?.id;

  useEffect(() => {
    dispatch(userFetch());

    try {
      // Create a BroadcastChannel to observe base/proxy account changes
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        baseProxySwitchChannel.current = new BroadcastChannel(CHANNELS.baseProxySwitch);
        baseProxySwitchChannel.current.onmessage = (event) => {
          baseProxySwitchChannel.current?.close();
          window.location.reload();
        };
      }
    } catch (error) {
      console.error('Error initializing BroadcastChannel:', error);
    }

    return () => {
      // Cleanup the BroadcastChannel
      baseProxySwitchChannel.current?.close();
      baseProxySwitchChannel.current = null;
    };
  }, []);

  useEffect(() => {
    if (isBaseProxySwitched) {
      baseProxySwitchChannel.current?.postMessage({ reload: true });
      dispatch(switchUserBroadcastSucceeded());
    }
  }, [isBaseProxySwitched]);

  if (user.isAuthenticating || user?.isLoading || routes < 1) return <LoaderPage />;

  if (isMaintenanceMode && !permissions?.canBypassMaintenance) return <Maintenance />;

  return (
    <StatsigProvider
      options={{
        environment: { tier: process.env.REACT_APP_ENVIRONMENT },
      }}
      sdkKey={process.env.REACT_APP_STATSIG}
      user={{
        custom: {
          client_id: clientInfo?.id,
          client_name: clientInfo?.name,
          name: `${firstName} ${lastName}`,
        },
        email,
        userId: id,
      }}
    >
      <div className={styles['dashboard']}>
        {isMaintenanceMode && (
          <Announcement
            header="Maintenance Mode"
            isCentered
            text="You are currently bypassing Maintenance Mode"
            variant="warn"
          />
        )}
        {baseAccountId && (
          <Announcement
            className={styles['dashboard-proxy']}
            header="Client Account"
            isCentered
            text={
              <>
                <span>You are logged in as {email} - </span>
                <button onClick={() => dispatch(switchToBaseUser({ baseAccountId, navigate }))}>
                  Switch to Personal Account
                </button>
              </>
            }
            variant="info"
          />
        )}
        <div className={styles['dashboard-body']}>
          <Header routes={routes} />
          <main id="dashboard">
            <Router />
          </main>
        </div>
        <Footer />
        <Notification />
        <ModalOnboarding />
      </div>
    </StatsigProvider>
  );
};

export default Dashboard;
